import React from 'react';
import { DateTime } from 'luxon';
import cx from 'classnames';
import { Link } from 'react-router-dom';
import { DropDown } from '~/components';
import { MdAssignment, MdAttachment, MdCancel, MdContentCopy } from 'react-icons/md';
import { get } from 'lodash';
import { useRouter, useAuth } from '~/hooks';
import { prefillFromSearch, prefillFromPTO } from '~/pages/FinalTitleOpinion/prefill';
import { useDashboard } from './context';
import { updateTitleOpinion, duplicateTitleOpinion as duplicateTitleOpinionApi } from '~/api/titleOpinion';
import dataFieldsToDuplicate from '~/util/dataFieldsToDuplicate';
import titleOpinionStatus from '~/util/titleOpinionStatus';

export const DateCell = (fieldName) => ({ data }) => {
    const value = data[fieldName];

    if (!value)
        return (<></>);

    const parsed = DateTime.fromISO(value);

    return (
        `${parsed.toLocaleString()}`
    );
};

const getLastStatus = data => {
    const statusHistory = get(data, 'titleOpinion.statusHistory', []);

    if (statusHistory.length === 0) return 1;

    for (let i = statusHistory.length - 1; i >= 0; i--) {
        if (statusHistory[i].statusId !== 6) return statusHistory[i].statusId;
    }
    return 1;
};

export const ActionCell = ({ data, ...rest }) => {
    const { hasRole } = useAuth();
    const isAdmin = hasRole(1) || hasRole(3);

    const hasPolicySearch = get(data, 'policySearch.id', null) != null;

    const hasPto = get(data, 'titleOpinion.hasPTO', false);
    const hasFto = get(data, 'titleOpinion.hasFTO', false);
    const hasSfto = get(data, 'titleOpinion.hasSFTO', false);

    const submitPTO = !hasPto;
    const submitFTO = !hasFto;

    const statusId = get(data, 'titleOpinion.statusId', 0);
    const canBeRevised = statusId === 2 || statusId === 5;
    const isCanceled = statusId === 6;

    const { history } = useRouter();

    const { setAttachments, showAttachmentsModal, setActiveTitleOpinionId, showRevisionsModal, fetchAll } = useDashboard();

    const hasDocs = Array.isArray(data.attachments) && data.attachments.length > 0;

    const cancelTitleOpinion = async () => {
        await updateTitleOpinion(data.titleOpinion.id, { statusId: 6 });
        await fetchAll();
    };

    const reopenTitleOpinion = async () => {
        await updateTitleOpinion(data.titleOpinion.id, { statusId: getLastStatus(data) });
        await fetchAll();
    };

    const duplicatePTO = async(titleOpinionId) => {
        const payload = {
            statusId: titleOpinionStatus.PTO.DRAFTED,
            titleOpinionId_original: titleOpinionId,
            dataToDuplicate: dataFieldsToDuplicate.PTO_DATA_FIELDS_DUPLICATE.dataToDuplicate
        };

        const result = await duplicateTitleOpinionApi(payload);
        if(+result?.id > 0) {
            history.push("/preliminaryopinion/" + result.id);
        } else {
            alert("unable to duplicate PTO");
        }
    };

    const duplicateSTF = async(titleOpinionId) => {
        const payload = {
            statusId: titleOpinionStatus.SFTO.DRAFTED,
            titleOpinionId_original: titleOpinionId,
            dataToDuplicate: dataFieldsToDuplicate.STF_DATA_FIELDS_DUPLICATE.dataToDuplicate
        };

        const result = await duplicateTitleOpinionApi(payload);
        if(+result?.id > 0) {
            history.push("/StraightFinal/" + result.id);
        } else {
            alert("Sorry. Unable to duplicate Straight To Final Opinion");
        }
    };

    return (
        <span {...rest}>
            <DropDown
                wrapperClass="rounded-md border bg-white text-neutral-80 text-sm z-20 filter drop-shadow-xl right-0 top-full mt-1"
                iconClass="text-white"
                iconSize='1.25em'
                buttonClass="p-1 bg-gray-10 text-white focus:outline-none hover:bg-secondary-30 rounded-full flex items-center justify-center"
                text=''>
                <section className="flex flex-col items-stretch w-48 text-gray-80 text-sm">
                    <p className="font-semibold p-4 py-2">Action</p>
                    {hasPolicySearch && (
                        <Link to={`/policySearchRequest/${data.policySearch.id}`} className="py-2 px-4 hover:bg-blue-10 flex items-center">
                            <MdAssignment />
                            <span className="ml-2">View Request</span>
                        </Link>
                    )}
                    {hasPto && (
                        <Link to={`/preliminaryOpinion/${data.titleOpinion.id}`} className="py-2 px-4 hover:bg-blue-10 flex items-center">
                            <MdAssignment />
                            <span className="ml-2">View PTO</span>
                        </Link>
                    )}
                    {hasPto && (
                        <button
                            className="py-2 px-4 hover:bg-blue-10 flex items-center"
                            onClick={() => duplicatePTO(data.titleOpinion.id)}
                        >
                            <MdContentCopy />
                            <span className="ml-2">Duplicate PTO</span>
                        </button>
                    )}
                    {hasSfto && (
                        <Link to={`/StraightFinal/${data.titleOpinion.id}`} className="py-2 px-4 hover:bg-blue-10 flex items-center">
                            <MdAssignment />
                            <span className="ml-2">View STF</span>
                        </Link>
                    )}
                    {hasSfto && (
                        <button
                            className="py-2 px-4 hover:bg-blue-10 flex items-center"
                            onClick={() => duplicateSTF(data.titleOpinion.id)}
                        >
                            <MdContentCopy />
                            <span className="ml-2">Duplicate STF</span>
                        </button>                        
                    )}
                    {(submitPTO && !hasSfto) &&(
                        <Link to='/preliminaryOpinion/0' className="py-2 px-4 hover:bg-blue-10 flex items-center">
                            <MdAssignment />
                            <span className="ml-2">Submit PTO</span>
                        </Link>
                    )}
                    {canBeRevised && (
                        <Link to={`/preliminaryOpinion/${data.titleOpinion.id}?action=revision`} className="py-2 px-4 hover:bg-blue-10 flex items-center">
                            <MdAssignment />
                            <span className="ml-2">Submit Revision</span>
                        </Link>
                    )}
                    {hasPto && (
                        <button
                            className="py-2 px-4 hover:bg-blue-10 flex items-center"
                            onClick={() => {
                                setActiveTitleOpinionId(data.titleOpinion.id);
                                showRevisionsModal();
                            }}
                        >
                            <MdAssignment />
                            <span className="ml-2">View Revisions</span>
                        </button>
                    )}
                    {hasFto && (
                        <Link to={`/finalopinion/${data.titleOpinion.id}`} className="py-2 px-4 hover:bg-blue-10 flex items-center">
                            <MdAssignment />
                            <span className="ml-2">View FTO</span>
                        </Link>
                    )}
                    
                    {(submitFTO && !hasSfto) &&(
                        <button
                            // to='/finalopinion/0' 
                            className="py-2 px-4 hover:bg-blue-10 flex items-center"
                            onClick={async () => {
                                if (hasPto) {
                                    await prefillFromPTO(data.titleOpinion.id);
                                    window.scrollTo(0, 0);
                                    history.push(`/finalopinion/${data.titleOpinion.id}`);
                                } else if (hasPolicySearch) {
                                    const titleOpinionId = await prefillFromSearch(data.policySearch.id, data.filenumber, data.titleOpinion?.orderNumber);
                                    window.scrollTo(0, 0);
                                    history.push(`/finalopinion/${titleOpinionId}`);
                                }
                            }}
                        >
                            <MdAssignment />
                            <span className="ml-2">Submit FTO</span>
                        </button>
                    )}
                    {hasDocs && (
                        <button
                            className='py-2 px-4 hover:bg-blue-10 focus:outline-none flex items-center rounded-b-md'
                            onClick={() => {
                                setAttachments(data.attachments);
                                showAttachmentsModal();
                            }}>
                            <MdAttachment className='mr-2' />
                            Attachments
                        </button>
                    )}
                    {((hasPto || hasFto) && !isCanceled) && (
                        <button className="py-2 px-4 hover:bg-error-10 flex items-center" onClick={cancelTitleOpinion}>
                            <MdCancel />
                            <span className="ml-2">Cancel PTO/FTO</span>
                        </button>
                    )}
                    {(isCanceled && isAdmin) && (
                        <button className="py-2 px-4 hover:bg-blue-10 focus:outline-none flex items-center rounded-b-md" onClick={reopenTitleOpinion}>
                            <MdCancel />
                            <span className="ml-2">Re-open PTO/FTO</span>
                        </button>
                    )}
                </section>
            </DropDown>
        </span>
    );
};

const statusLabels = {
    ['t-1']: 'PTO Started',
    ['t-2']: 'PTO Submitted',
    ['t-3']: 'FTO Started',
    ['t-4']: 'FTO Submitted',
    ['t-5']: 'PTO Revision Submitted',
    ['t-6']: 'Canceled',
    ['t-7']: 'STF Started',
    ['t-8']: 'STF Submitted',
    ['p-2']: 'Prior Policy Requested',
    ['p-3']: 'Prior Policy Found',
    ['p-4']: 'Prior Policy Not Found'
};

const StatusPill = ({ status, fallback }) => {
    let label = fallback;

    if (statusLabels[status] != undefined) label = statusLabels[status];

    const classes = cx(
        'rounded-full',
        'py-1', 'px-3',
        'text-xs',
        'border',
        {
            ['bg-error-10 text-error-60 border-error-30']: /t-6/i.test(status),
            ['bg-warning-10 text-warning-60 border-warning-30']: /(p-(1|2))/i.test(status),
            ['bg-info-10 text-info-60 border-info-30']: /p-3/i.test(status),
            ['bg-primary-10 text-primary-60 border-primary-30']: /t-(1|3|7)/i.test(status),
            ['bg-success-10 text-success-60 border-success-30']: /t-(2|4|5|8)/i.test(status),
            ['bg-secondary-10 text-secondary-80 border-secondary-30']: /t-4 /i.test(status)
        }
    );

    return <div style={{ width: 'max-content' }} className={classes}>{label}</div>;
};

export const StatusCell = ({ data, ...rest }) => {

    return (
        <span {...rest}>
            <StatusPill status={data.status} fallback='derp' />
        </span>
    );
};

export const OwnerCell = ({ data, ...rest }) => {
    return (
        <span {...rest}>
            <OwnerName owners={data.owners} />
        </span>
    );
};

function OwnerName({ owners }) {
    return Array.isArray(owners)
        ? <>{owners.map(mapOwner).filter(o => typeof o == 'string' && o.trim().length > 0).join(', ')}</>
        : <></>;
}

export function mapOwner(owner) {
    if (!owner)
        return '';

    if (owner.name) {
        return `${owner.name}`;
    } else if (owner.entityName) {
        return `${owner.entityName}`;
    } else if (owner.firstName) {
        const { firstName, middleName, lastName } = owner;

        return `${firstName} ${middleName} ${lastName}`;
    }

    let result = [];
    if (owner.primary) {
        result.push(`${owner.primary.firstName} ${owner.primary.middleName} ${owner.primary.lastName}`);
    }
    if (owner.secondary) {
        result.push(`${owner.secondary.firstName} ${owner.secondary.middleName} ${owner.secondary.lastName}`);
    }

    return result.join(', ');
}